<template>
  <div>
    <!-- MINIMIZED BUTTON -->
    <div
      v-if="(hasFiles && (isMinimized || !isOpen)) || !hasFiles"
      class="upload-fab shadow"
      @click="onFabClick"
    >
      <i class="bx bx-cloud-upload"></i>
      <span v-if="uploading.length" class="badge bg-warning font-size-12">
        {{ uploading.length }}
      </span>
      <span v-else-if="completed.length" class="badge bg-success font-size-12">
        {{ completed.length }}
      </span>
      <span v-else-if="failed.length" class="badge bg-danger font-size-12">
        {{ failed.length }}
      </span>
    </div>

    <!-- FULL PANEL -->
    <transition name="slide-up">
      <div
        v-if="!isMinimized && hasFiles && isOpen"
        class="upload-panel card shadow-lg"
      >
        <!-- HEADER -->
        <div class="card-header bg-light d-flex justify-content-between align-items-center">
          <strong>
          <i class="mdi mdi-robot-outline me-1"></i>
            Smart Upload
          </strong>

          <div class="d-flex align-items-center">
            <b-button
              size="sm"
              variant="success"
              class="me-2 d-flex align-items-center justify-content-center text-white"
              style="padding: 4px 6px; border-radius: 4px;"
              @click="openUploadModal"
              title="Upload files"
            >
              <i class="bx bx-cloud-upload font-size-16"></i>
            </b-button>

            <i
              class="bx bx-minus font-size-16 me-2"
              style="cursor:pointer"
              @click="toggleMinimize"
            ></i>

            <!-- <i
              class="bx bx-x font-size-16 text-danger"
              style="cursor:pointer"
              @click="closePanel"
            ></i> -->
          </div>
        </div>

        <div class="card-body p-0 upload-body">
          <div class="upload-scroll p-2">

            <!-- FAILED SECTION -->
            <div v-if="failed.length" :class="{ 'mt-2': uploading.length || completed.length }">
              <div class="section-title text-danger small mb-2">
                Failed
              </div>

              <div
                v-for="file in failed"
                :key="file.fileId"
                class="upload-item"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <span class="text-truncate text-danger" style="max-width: 80%;">
                    <i :class="`mdi font-size-14 me-1 ${getFileIcon(file.fileName)}`"></i>
                    {{ file.fileName }}
                  </span>
                  <div class="d-flex align-items-center gap-2">
                    <!-- Debug Button -->
                    <div v-if="$isAdmin() && $isDebug()" class="d-flex ms-2">
                      <button
                        class="btn btn-sm px-2 py-0 font-size-11 admin-debug-btn"
                        title="View object data"
                        @click.stop="$root.$emit('on-debug', file)"
                      >
                        Debug
                      </button>
                    </div>
                    <i class="bx bx-error-circle text-danger font-size-16"></i>
                    <b-button
                      size="sm"
                      variant="danger"
                      class="text-white d-flex align-items-center justify-content-center"
                      style="padding: 4px 6px; border-radius: 4px;"
                      @click="removeFile(file)"
                      title="Remove"
                    >
                      <i class="bx bx-trash font-size-12"></i>
                    </b-button>
                  </div>
                </div>

                <small v-if="file.errorMessage" class="text-danger d-block mt-1">
                  {{ file.errorMessage }}
                </small>

                <b-progress
                  :value="100"
                  height="6px"
                  class="mt-1"
                  variant="danger"
                />
              </div>
            </div>

            <!-- UPLOADING SECTION -->
            <div v-if="uploading.length">
              <div class="section-title text-muted small mb-2">
                Uploading...
              </div>

              <div
                v-for="file in uploading"
                :key="file.fileId"
                class="upload-item"
              >
                <div class="d-flex justify-content-between">
                  <span class="text-truncate">
                    <i :class="`mdi font-size-14 me-1 ${getFileIcon(file.fileName)}`"></i>
                    {{ file.fileName }}
                  </span>
                  <!-- Debug Button -->
                  <div v-if="$isAdmin() && $isDebug()" class="d-flex ms-2">
                    <button
                      class="btn btn-sm px-2 py-0 font-size-11 admin-debug-btn"
                      title="View object data"
                      @click.stop="$root.$emit('on-debug', file)"
                    >
                      Debug
                    </button>
                  </div>
                  <small>
                    {{ file.status === 'processing' ? 'Processing...' : file.progress + '%' }}
                  </small>
                </div>

                <b-progress
                  :value="file.status === 'processing' ? 100 : file.progress"
                  height="6px"
                  class="mt-1"
                  :variant="file.status === 'processing' ? 'warning' : 'primary'"
                  animated
                />
              </div>
            </div>

            <!-- COMPLETED SECTION -->
            <div v-if="completed.length" :class="{ 'mt-2': uploading.length }">
              <div class="section-title text-success small mb-2">
                <i class="mdi mdi-check-circle me-1"></i> Done
              </div>

              <div
                v-for="file in completed"
                :key="file.fileId"
                class="upload-item d-flex justify-content-between align-items-center"
              >
                <span class="text-truncate" style="max-width: 65%;">
                  <i :class="`mdi font-size-14 me-1 ${getFileIcon(file.fileName)}`"></i>
                  {{ file.fileName }}
                </span>

                <div class="d-flex align-items-center gap-2">
                  <!-- Debug Button -->
                  <div v-if="$isAdmin() && $isDebug()" class="d-flex ms-2">
                    <button
                      class="btn btn-sm px-2 py-0 font-size-11 admin-debug-btn"
                      title="View object data"
                      @click.stop="$root.$emit('on-debug', file)"
                    >
                      Debug
                    </button>
                  </div>
                  <b-button
                    size="sm"
                    variant="primary"
                    @click="review(file)"
                    class="text-white"
                  >
                    Review
                  </b-button>
                  <b-button
                    size="sm"
                    variant="danger"
                    class="text-white d-flex align-items-center justify-content-center"
                    style="padding: 4px 6px;"
                    @click="removeFile(file)"
                    title="Remove"
                  >
                    <i class="bx bx-trash font-size-14"></i>
                  </b-button>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </div>
    </transition>

    <!-- AI Upload Modal -->
    <AiFileModal
      :modalID="addFilesModalID"
      modal-title="Smart Upload"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex"
import AiFileModal from "@/components/file-manager/file/ai-file-modal.vue"

export default {
  components: {
    AiFileModal
  },

  data() {
    return {
      addFilesModalID: "floating-smart-upload-modal"
    };
  },

  computed: {
    ...mapState("aiFileManager/smartUpload", ["uploads", "isOpen", "isMinimized"]),
    ...mapGetters("aiFileManager/smartUpload", ["uploading", "completed", "failed"]),
    hasFiles() {
      return this.uploading.length > 0 || this.completed.length > 0 || this.failed.length > 0
    }
  },

  mounted() {
    this.$store.dispatch("aiFileManager/smartUpload/registerSignalR")
    this.$store.dispatch("aiFileManager/smartUpload/fetchUploads")
    window.addEventListener("beforeunload", this.onBeforeUnload)
  },

  beforeDestroy() {
    this.$store.dispatch("aiFileManager/smartUpload/unregisterSignalR")
    window.removeEventListener("beforeunload", this.onBeforeUnload)
  },

  methods: {
    onFabClick() {
      // If there is no upload yet, open the upload modal directly.
      if (!this.hasFiles) {
        this.openUploadModal();
        return;
      }

      // Recover from externally closed state so users always have a way back.
      if (!this.isOpen) {
        this.$store.commit("aiFileManager/smartUpload/OPEN_PANEL")
        this.$store.commit("aiFileManager/smartUpload/SET_MINIMIZED", false)
        return;
      }

      this.toggleMinimize();
    },
    openUploadModal() {
      this.$bvModal.show(this.addFilesModalID);
      this.toggleMinimize();
    },
    onBeforeUnload(e) {
      const hasActiveUpload = this.uploads.some(u => u.status === 'uploading')
      if (hasActiveUpload) {
        e.preventDefault()
        e.returnValue = ''
      }
    },
    toggleMinimize() {
      this.$store.commit("aiFileManager/smartUpload/TOGGLE_MINIMIZE")
    },
    closePanel() {
      this.$store.commit("aiFileManager/smartUpload/CLOSE_PANEL")
    },
    getFileIcon(fileName) {
      return this.$getFileIcon(fileName)
    },
    review(item) {
      this.$root.$emit(`ai-files-upload`, item);

      this.$nextTick(() => {
        this.toggleMinimize();
      });
    },
    removeFile(file) {
      this.$deleteModal("uploaded file", "remove").then((res) => {
        if (res && res.isConfirmed) {
          const fileId = file.fileId;
          const id = file.id;
          this.$store.dispatch("aiFileManager/smartUpload/deleteUpload", {
            id,
            fileId,
          }).then(() => {
            this.$toast.success("File successfully removed");
          }).catch((err) => {
            console.error("Error removing file:", err);
            this.$toast.error("Failed to remove file");
          });
        }
      });
    }
  }
}
</script>

<style scoped>
.upload-panel {
  position: fixed;
  bottom: 25px;
  left: calc(60px + 25px);
  width: 380px;
  border-radius: 12px;
  z-index: 1055;

  display: flex;
  flex-direction: column;
  max-height: 500px; /* 👈 overall limit */
}

/* Make body flex */
.upload-body {
  flex: 1;
  overflow: hidden;
  padding: 0;
}

/* Scrollable area */
.upload-scroll {
  max-height: 380px; /* 👈 controls scroll height */
  overflow-y: auto;
  padding: 12px;
}

/* Smooth modern scrollbar */
.upload-scroll::-webkit-scrollbar {
  width: 6px;
}

.upload-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}

.upload-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.35);
}

.upload-item {
  padding: 8px 0;
  border-bottom: 1px solid #f1f1f1;
}

.upload-item:last-child {
  border-bottom: none;
}

/* FAB stays same */
.upload-fab {
  position: fixed;
  bottom: 25px;
  left: calc(60px + 25px);
  width: 48px;
  height: 48px;
  background: #556ee6;
  color: white;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 20px;
  z-index: 1060;
  box-shadow: 0 3px 12px rgba(85, 110, 230, 0.4);
  transition: all 0.3s ease;
}

.upload-fab:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 16px rgba(85, 110, 230, 0.5);
  background: #4961d8;
}

.upload-fab .badge {
  position: absolute;
  top: -5px;
  right: -5px;
}

/* animation */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .upload-panel {
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    max-width: none;
    height: 100dvh;
    max-height: 100dvh;
    border-radius: 0;
  }

  .upload-body {
    min-height: 0;
  }

  .upload-scroll {
    max-height: none;
    height: 100%;
    padding-bottom: calc(12px + env(safe-area-inset-bottom));
  }

  .upload-fab {
    left: 16px;
    right: auto;
    bottom: calc(16px + env(safe-area-inset-bottom));
  }
}
</style>